import Load from 'components/Load';
import {
  NavigationalLayoutElement,
  type NavigationalLayoutProps,
} from 'modules/app/components/NavigationalLayout';
import AppBottomPushDown from 'modules/common/Navbar/PushDown/AppBottomPushDown';
import Notfound from 'modules/errorpages/Notfound';
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { RouteDescriptor, createRouteHandle } from 'routing/types';
import basePath from 'utils/basePath';
import { relativePathFromBase } from 'utils/relativePathFromBase';

const AsyncTransportationModePage = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-transport-mode" */ './containers/TransportationModePage'
    ),
);
const AsyncLicenseFormPage = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-license" */ './containers/LicenseFormPage'
    ),
);
const AsyncLanguagesPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-languages" */ './containers/LanguagesPage'
    ),
);
const AsyncExpertisePageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-expertise" */ './containers/ExpertisePage'
    ),
);
const AsyncBasicInfoPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-basic-info" */ './containers/BasicInfoPage'
    ),
);
const AsyncPublicInfoPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-public-info" */ './containers/PublicInfoPage'
    ),
);
const AsyncWebinarPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-webinar-info" */ './containers/WebinarPageContainer'
    ),
);
const AsyncThankYouPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-thank-you-info" */ './containers/ThankYouPageContainer'
    ),
);
const AsyncContactPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-contact-info" */ './containers/ContactInfoPage'
    ),
);
const AsyncVerifyPhonePageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-phone" */ './containers/VerifyPhonePage'
    ),
);
const AsyncVerifyEmailPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-phone" */ './containers/VerifyEmailPage'
    ),
);
const AsyncProfileManagementPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-management" */ './containers/ProfileManagementPage'
    ),
);
const AsyncHostCityPageContainer = Load(
  () =>
    import(
      /* webpackChunkName: "host-profile-management" */ './containers/HostCityPage'
    ),
);

const base = `${basePath}/host/profile`;
type ChildRouteOptions = Omit<NavigationalLayoutProps, 'component'> & {
  path?: string;
  index?: boolean;
};

const createChildRoute = (
  descriptor: RouteDescriptor,
  Component: React.ComponentType<any>,
  options: ChildRouteOptions = {},
) => {
  const { path: override, index: forcedIndex, ...layoutProps } = options;
  const relative = override ?? relativePathFromBase(base, descriptor.path);
  const isIndex = forcedIndex ?? relative === '';

  const element = (
    <NavigationalLayoutElement {...layoutProps} component={Component as any} />
  );

  if (isIndex) {
    return (
      <Route
        key={`${descriptor.id}__index`}
        id={descriptor.id}
        index
        handle={createRouteHandle(descriptor)}
        element={element}
      />
    );
  }

  return (
    <Route
      key={descriptor.id}
      id={descriptor.id}
      path={relative}
      handle={createRouteHandle(descriptor)}
      element={element}
    />
  );
};

export const hostProfileRootRoute: RouteDescriptor = {
  id: 'host-profile.root',
  path: `${base}/*`,
  label: 'Host Profile Root',
  audience: 'host',
  layout: 'navigational',
  module: 'host-profile',
};

const makeDescriptor = (
  id: string,
  suffix: string,
  label: string,
): RouteDescriptor => ({
  id,
  path: `${base}${suffix}`,
  label,
  audience: 'host',
  layout: 'navigational',
  module: 'host-profile',
});

export const hostProfileTransportationRoute = makeDescriptor(
  'host-profile.transportation-mode',
  '/transportation-mode/',
  'Transportation Mode',
);
export const hostProfileLicenseRoute = makeDescriptor(
  'host-profile.license',
  '/license/',
  'License',
);
export const hostProfileWebinarRoute = makeDescriptor(
  'host-profile.webinar',
  '/webinar/',
  'Webinar',
);
export const hostProfileThankYouRoute = makeDescriptor(
  'host-profile.thank-you',
  '/thank-you/',
  'Thank You',
);
export const hostProfileLanguagesRoute = makeDescriptor(
  'host-profile.languages',
  '/languages/',
  'Languages',
);
export const hostProfileExpertiseRoute = makeDescriptor(
  'host-profile.expertise',
  '/expertise/',
  'Expertise',
);
export const hostProfileBasicInfoRoute = makeDescriptor(
  'host-profile.basic-info',
  '/basic-info/',
  'Basic Info',
);
export const hostProfilePublicInfoRoute = makeDescriptor(
  'host-profile.public-info',
  '/public-info/',
  'Public Info',
);
export const hostProfileContactRoute = makeDescriptor(
  'host-profile.contact-info',
  '/contact-info/',
  'Contact Info',
);
export const hostProfilePhoneRoute = makeDescriptor(
  'host-profile.phone',
  '/phone/',
  'Verify Phone',
);
export const hostProfileEmailRoute = makeDescriptor(
  'host-profile.email',
  '/email/',
  'Verify Email',
);
export const hostProfileCityRoute = makeDescriptor(
  'host-profile.city',
  '/city/',
  'Host City',
);
export const hostProfileManagementRoute = makeDescriptor(
  'host-profile.management',
  '/',
  'Profile Management',
);

const SubRoutes = () => {
  return (
    <Routes>
      {createChildRoute(
        hostProfileTransportationRoute,
        AsyncTransportationModePage,
        {
          path: 'transportation-mode/*',
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(hostProfileLicenseRoute, AsyncLicenseFormPage, {
        path: 'license/*',
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostProfileWebinarRoute, AsyncWebinarPageContainer, {
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostProfileThankYouRoute, AsyncThankYouPageContainer, {
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(
        hostProfileLanguagesRoute,
        AsyncLanguagesPageContainer,
        {
          path: 'languages/*',
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(
        hostProfileExpertiseRoute,
        AsyncExpertisePageContainer,
        {
          path: 'expertise/*',
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(
        hostProfileBasicInfoRoute,
        AsyncBasicInfoPageContainer,
        {
          path: 'basic-info/*',
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(
        hostProfilePublicInfoRoute,
        AsyncPublicInfoPageContainer,
        {
          path: 'public-info/*',
          hideFooter: true,
          noNavbar: true,
          nonFixedNavbar: true,
        },
      )}
      {createChildRoute(hostProfileContactRoute, AsyncContactPageContainer, {
        path: 'contact-info/*',
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostProfilePhoneRoute, AsyncVerifyPhonePageContainer, {
        path: 'phone/*',
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostProfileEmailRoute, AsyncVerifyEmailPageContainer, {
        path: 'email/*',
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(hostProfileCityRoute, AsyncHostCityPageContainer, {
        hideFooter: true,
        noNavbar: true,
        nonFixedNavbar: true,
      })}
      {createChildRoute(
        hostProfileManagementRoute,
        AsyncProfileManagementPageContainer,
        {
          hideFooter: true,
          index: true,
        },
      )}
      <Route path="*" element={<Notfound />} />
    </Routes>
  );
};

const HostProfileRoutes = () => (
  <>
    <SubRoutes />
    <AppBottomPushDown />
  </>
);

export const hostProfileRoutes = [
  <Route
    key={hostProfileRootRoute.id}
    id={hostProfileRootRoute.id}
    path={hostProfileRootRoute.path}
    handle={createRouteHandle(hostProfileRootRoute)}
    element={<HostProfileRoutes />}
  />,
];

export const hostProfileRouteDescriptors = [
  hostProfileRootRoute,
  hostProfileTransportationRoute,
  hostProfileLicenseRoute,
  hostProfileWebinarRoute,
  hostProfileThankYouRoute,
  hostProfileLanguagesRoute,
  hostProfileExpertiseRoute,
  hostProfileBasicInfoRoute,
  hostProfilePublicInfoRoute,
  hostProfileContactRoute,
  hostProfilePhoneRoute,
  hostProfileEmailRoute,
  hostProfileCityRoute,
  hostProfileManagementRoute,
];

export default hostProfileRoutes;
